import React from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: theme.palette.background.default
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: (theme.shape.borderRadius as number) * 2,
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none'
  },
  '&.Mui-expanded': {
    margin: `0 0 ${theme.spacing(2)}px 0`
  }
}));

const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: "How does RiteReview ensure authentic reviews?",
      answer: "RiteReview only works with your actual customers who have purchased your products or services. Our AI generates reviews based on genuine customer responses to surveys, ensuring authenticity while maintaining natural language that reflects real experiences."
    },
    {
      question: "Which platforms does RiteReview support?",
      answer: "RiteReview integrates with major review platforms including Google My Business, Facebook, Instagram, Yelp, and other popular review sites. You can configure which platforms to target for each campaign based on your business needs."
    },
    {
      question: "How does the payment system work for customers?",
      answer: "Customers receive instant payouts via UPI (Unified Payments Interface) immediately after posting approved reviews. The payment amount is configurable by you as the business owner, and transactions are processed securely through our integrated payment gateway."
    },
    {
      question: "Is RiteReview compliant with platform policies?",
      answer: "Yes, RiteReview operates within the guidelines of major review platforms. We focus on facilitating genuine feedback from real customers rather than creating fake reviews. Our AI enhancement ensures reviews sound natural while maintaining authenticity."
    },
    {
      question: "What kind of analytics and reporting do you provide?",
      answer: "Our comprehensive admin dashboard provides real-time analytics including review generation rates, platform performance, customer engagement metrics, ROI tracking, and detailed campaign reports to help you optimize your review strategy."
    },
    {
      question: "How much does RiteReview cost?",
      answer: "We offer flexible pricing plans based on your business size and review volume needs. Early waitlist members will receive a 50% discount on their first 3 months. Detailed pricing will be announced closer to launch."
    },
    {
      question: "Can I customize the survey questions?",
      answer: "Absolutely! You can create custom surveys tailored to your specific products or services, or use our AI-powered survey generator that creates relevant questions based on your business type and customer data."
    },
    {
      question: "What happens if a customer gives a negative review?",
      answer: "Our system is designed to identify potentially negative feedback during the survey stage. You can choose to address concerns privately with the customer before any public review is posted, helping you maintain your reputation while improving customer satisfaction."
    }
  ];

  return (
    <SectionContainer>
      <Container maxWidth="md">
        <Box textAlign="center" sx={{ mb: 8 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 3,
              color: 'text.primary'
            }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: 600, 
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Got questions? We've got answers. Learn more about how RiteReview can transform your online presence.
          </Typography>
        </Box>

        <Box>
          {faqs.map((faq, index) => (
            <StyledAccordion key={index} elevation={0}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </Box>

        <Box 
          sx={{ 
            mt: 8, 
            p: 4, 
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Still have questions?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            Our team is here to help. Reach out to us for personalized assistance.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Email us at: <strong>team@ritereview.com</strong>
          </Typography>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default FAQSection;